import { TLocation } from 'lib/types/location';
import { CurrencyKeys } from 'lib/types/trip';
import { defaultLocation } from 'data/location';

export const getLocationFromTimezone = (): TLocation => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const timezoneMappings: Record<string, TLocation> = {
    // GBP countries
    'Europe/London': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'GB', name: 'United Kingdom' },
    },
    'Europe/Guernsey': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'GG', name: 'Guernsey' },
    },
    'Europe/Isle_of_Man': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'IM', name: 'Isle of Man' },
    },
    'Europe/Jersey': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'JE', name: 'Jersey' },
    },

    // NZD countries
    'Pacific/Auckland': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'NZ', name: 'New Zealand' },
    },
    'Pacific/Chatham': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'NZ', name: 'New Zealand' },
    },

    // AUD countries
    'Australia/Sydney': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Melbourne': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Perth': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Adelaide': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Brisbane': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Darwin': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Hobart': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Broken_Hill': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },
    'Australia/Lord_Howe': {
      continent: 'Oceania',
      region: 'Australia and New Zealand',
      country: { code: 'AU', name: 'Australia' },
    },

    // CAD countries
    'America/Toronto': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },
    'America/Vancouver': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },
    'America/Calgary': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },
    'America/Montreal': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },
    'America/Halifax': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },
    'America/Winnipeg': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },
    'America/Edmonton': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },
    'America/St_Johns': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'CA', name: 'Canada' },
    },

    // USD countries
    'America/New_York': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'US', name: 'United States' },
    },
    'America/Los_Angeles': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'US', name: 'United States' },
    },
    'America/Chicago': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'US', name: 'United States' },
    },
    'America/Denver': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'US', name: 'United States' },
    },
    'America/Phoenix': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'US', name: 'United States' },
    },
    'America/Anchorage': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'US', name: 'United States' },
    },
    'America/Honolulu': {
      continent: 'Americas',
      region: 'Northern America',
      country: { code: 'US', name: 'United States' },
    },

    // EUR countries
    'Europe/Paris': {
      continent: 'Europe',
      region: 'Western Europe',
      country: { code: 'FR', name: 'France' },
    },
    'Europe/Berlin': {
      continent: 'Europe',
      region: 'Western Europe',
      country: { code: 'DE', name: 'Germany' },
    },
    'Europe/Rome': {
      continent: 'Europe',
      region: 'Southern Europe',
      country: { code: 'IT', name: 'Italy' },
    },
    'Europe/Madrid': {
      continent: 'Europe',
      region: 'Southern Europe',
      country: { code: 'ES', name: 'Spain' },
    },
    'Europe/Amsterdam': {
      continent: 'Europe',
      region: 'Western Europe',
      country: { code: 'NL', name: 'Netherlands' },
    },
    'Europe/Brussels': {
      continent: 'Europe',
      region: 'Western Europe',
      country: { code: 'BE', name: 'Belgium' },
    },
    'Europe/Vienna': {
      continent: 'Europe',
      region: 'Western Europe',
      country: { code: 'AT', name: 'Austria' },
    },
    'Europe/Lisbon': {
      continent: 'Europe',
      region: 'Southern Europe',
      country: { code: 'PT', name: 'Portugal' },
    },
    'Europe/Dublin': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'IE', name: 'Ireland' },
    },
    'Europe/Stockholm': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'SE', name: 'Sweden' },
    },
    'Europe/Oslo': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'NO', name: 'Norway' },
    },
    'Europe/Copenhagen': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'DK', name: 'Denmark' },
    },
    'Europe/Helsinki': {
      continent: 'Europe',
      region: 'Northern Europe',
      country: { code: 'FI', name: 'Finland' },
    },
    'Europe/Warsaw': {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: { code: 'PL', name: 'Poland' },
    },
    'Europe/Prague': {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: { code: 'CZ', name: 'Czech Republic' },
    },
    'Europe/Budapest': {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: { code: 'HU', name: 'Hungary' },
    },
    'Europe/Zurich': {
      continent: 'Europe',
      region: 'Western Europe',
      country: { code: 'CH', name: 'Switzerland' },
    },
    'Europe/Athens': {
      continent: 'Europe',
      region: 'Southern Europe',
      country: { code: 'GR', name: 'Greece' },
    },
    'Europe/Istanbul': {
      continent: 'Europe',
      region: 'Eastern Europe',
      country: { code: 'TR', name: 'Turkey' },
    },
  };

  return timezoneMappings[timezone] || defaultLocation;
};

export const getCurrencyByTimezone = (): CurrencyKeys => {
  const location = getLocationFromTimezone();
  return getCurrencyByCountryCode(location.country.code);
};

const getCurrencyByCountryCode = (countryCode: string): CurrencyKeys => {
  switch (countryCode) {
    case 'GB':
    case 'GG':
    case 'IM':
    case 'JE':
      return 'GBP';
    case 'NZ':
      return 'NZD';
    case 'AU':
      return 'AUD';
    case 'CA':
      return 'CAD';
    case 'US':
      return 'USD';
    case 'FR':
    case 'DE':
    case 'IT':
    case 'ES':
    case 'NL':
    case 'BE':
    case 'AT':
    case 'PT':
    case 'IE':
    case 'SE':
    case 'NO':
    case 'DK':
    case 'FI':
    case 'PL':
    case 'CZ':
    case 'HU':
    case 'CH':
    case 'GR':
    case 'TR':
      return 'EUR';
    default:
      return 'USD';
  }
};
